import type { AlertType as UIAlertType } from '$shared/components';
import { getRandomString } from '$shared/helpers/utils';
import BaseStore from './BaseStore';

const DefaultCloseIn = 3000; // 3seconds

type AlertOption = {
  message: string;
  type?: UIAlertType;
  closable?: boolean;
};

export type AlertType = AlertOption & {
  closeIn?: number;
  id: string;
};

type AlertStoreType = {
  list: AlertType[];
  showVersionUpdateAlert?: boolean;
};

const InitialState: AlertStoreType = {
  list: [],
  showVersionUpdateAlert: false
};

class GlobalAlertStore extends BaseStore<AlertStoreType> {
  constructor() {
    super({ ...InitialState });
  }

  public get list(): AlertType[] {
    return this.getValue('list') || [];
  }

  public setVersionUpdateAlert(show: boolean): void {
    this.store.update((state) => {
      state.showVersionUpdateAlert = show;
      return state;
    });
  }

  public get(id: string): AlertType | undefined {
    const list = this.getValue<AlertType[]>('list') || [];
    return list.find((item) => item.id === id);
  }

  public show(options: AlertOption, autoClose = false): string | undefined {
    if (!options?.message || (autoClose && this.isDuplicateAlert(options))) return;

    const id = getRandomString();
    const newOptions = {
      id,
      ...options,
      closeIn: (autoClose && DefaultCloseIn) || 0
    };
    this.store.update((state) => {
      state.list.push(newOptions);
      return state;
    });

    return newOptions.id;
  }

  remove(id: string): void {
    if (!id) return;
    this.store.update((state) => {
      const listClone = [...state.list];
      const index = listClone.findIndex((item) => item.id === id);
      listClone.splice(index, 1);
      state.list = [...listClone];
      return state;
    });
  }

  private isDuplicateAlert(options: AlertOption): boolean {
    // Check the message string, that was already in the list
    // then no need to push the item to the list, it will looks like same error with 3 or 4 alerts on UI
    return this.list.some((item: AlertType) => item.message === options?.message);
  }
}

export default new GlobalAlertStore();
